import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Video, Sparkles, CheckCircle2, X } from "lucide-react";
import { Button } from "./ui/button";
import { MobileDialog } from "./mobile";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_MB = 40;

/**
 * EndLessonClipUploader — shown on End Lesson flow when the learner has a
 * paid addon (progress_clip or premium_review) waiting.
 *
 * Design goal: < 20 seconds from "End Lesson" tap to "Uploaded".
 *
 * Props:
 *  - addonId: the paid passport_addons row id
 *  - product: "progress_clip" | "premium_review"
 *  - onComplete(result): called when upload (and optional auto-AI) finishes
 *  - onSkip(): close without uploading (learner keeps the purchased slot)
 */
export default function EndLessonClipUploader({ addonId, product, onComplete, onSkip }) {
  // P1 mobile sprint — body-scroll lock (parent unmounts when closed).
  useScrollLock(true);
  const inputRef = useRef();
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [tags, setTags] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(null);

  const premium = product === "premium_review";

  const upload = async () => {
    if (!file) {
      inputRef.current?.click();
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large (max ${MAX_MB} MB)`);
      return;
    }
    setSubmitting(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("addon_id", addonId);
      fd.append("tags", tags);
      fd.append("note", note);
      const r = await axios.post(`${API}/api/passport/clips/upload`, fd, {
        withCredentials: true,
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((100 * e.loaded) / e.total));
        },
      });
      setDone(r.data);
      // Fire telemetry (best-effort)
      try {
        await axios.post(
          `${API}/api/beta/telemetry`,
          { event: "clip_upload_completed", metadata: { product, size_bytes: file.size } },
          { withCredentials: true }
        );
      } catch {}
      toast.success(premium ? "Uploaded — AI review generating" : "Uploaded to your Passport");
      onComplete?.(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MobileDialog
      bareContent
      open
      onClose={onSkip}
      testid="end-lesson-clip-uploader"
      priority="urgent"
      presentation="center"
    >
      <div className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 p-5 mx-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${premium ? "bg-slate-900 dark:bg-white" : "bg-indigo-600"}`}>
              {premium ? <Sparkles className="w-4 h-4 text-white dark:text-slate-900" /> : <Video className="w-4 h-4 text-white" />}
            </div>
            <div>
              <p className="text-sm font-extrabold text-slate-900 dark:text-white">
                {premium ? "Upload for Premium Review" : "Add Clip to Passport"}
              </p>
              <p className="text-[11px] text-slate-500">Optional · &lt;20 sec flow</p>
            </div>
          </div>
          <button data-testid="clip-uploader-close" onClick={onSkip} className="text-slate-400 hover:text-slate-600" aria-label="Skip">
            <X className="w-5 h-5" />
          </button>
        </div>

        {done ? (
          <div className="py-6 text-center" data-testid="clip-uploader-done">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <p className="text-sm font-bold text-slate-900 dark:text-white">Saved to your Passport</p>
            {done.ai_review_auto_triggered && (
              <p className="text-xs text-slate-500 mt-1">AI review generating — you'll see it in a moment.</p>
            )}
            <Button
              onClick={onSkip}
              data-testid="clip-uploader-dismiss"
              className="mt-4 w-full h-10 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold"
            >
              Done
            </Button>
          </div>
        ) : (
          <>
            <input
              ref={inputRef}
              data-testid="clip-uploader-input"
              type="file"
              accept="video/*,image/*"
              hidden
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />

            <button
              data-testid="clip-uploader-pick"
              onClick={() => inputRef.current?.click()}
              disabled={submitting}
              className="w-full rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 p-4 text-sm text-slate-600 dark:text-slate-300 hover:border-slate-400 transition-colors"
            >
              {file ? `📎 ${file.name} · ${(file.size/1024/1024).toFixed(1)} MB` : "Tap to pick a clip or photo"}
            </button>

            <div className="grid grid-cols-2 gap-2 mt-3">
              <input
                data-testid="clip-uploader-tags"
                placeholder="Tags (comma)"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                className="h-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 text-sm"
              />
              <input
                data-testid="clip-uploader-note"
                placeholder="Quick note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="h-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 text-sm"
              />
            </div>

            {submitting && (
              <div className="mt-3" data-testid="clip-uploader-progress">
                <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                  <div className="h-full bg-indigo-500 transition-all" style={{ width: `${progress}%` }} />
                </div>
                <p className="text-[11px] text-slate-500 mt-1 text-right">{progress}%</p>
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <Button
                data-testid="clip-uploader-skip"
                onClick={onSkip}
                variant="outline"
                disabled={submitting}
                className="flex-1 h-11 rounded-xl text-sm"
              >
                Skip
              </Button>
              <Button
                data-testid="clip-uploader-submit"
                onClick={upload}
                disabled={submitting}
                className="flex-1 h-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : file ? "Upload" : "Pick file"}
              </Button>
            </div>

            <p className="text-[10px] text-slate-400 mt-3 text-center">
              {premium ? "AI review will generate automatically. Guidance only — never a pass guarantee." : "Saves to your learner-owned Passport. You can share or revoke any time."}
            </p>
          </>
        )}
      </div>
    </MobileDialog>
  );
}
